import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import {
  SLACKID_ONBOARDING_MODAL_GO_TO_WORKSPACE,
  SLACKID_ONBOARDING_MODAL_PERMANENTLY_HIDDEN,
  SLACKID_ONBOARDING_MODAL_VIEW_CHANGED,
} from 'constants/events';
import { EXP_SLACKID_ONBOARDING_MODAL } from 'constants/featureFlag';
import { SLACK_OIDC_CONNECT } from 'constants/oAuthKeys';
import { MY_VIDEOS_PAGE } from 'constants/routes';
import * as analytics from 'utilities/analytics';
import { isMobile } from 'utilities/device';
import * as localStorage from 'utilities/localStorage';
import { useSlackIdOnboardingModalState } from 'utilities/localStorage/onboarding';

import { selectCurrentUser } from 'selectors/user';

import OnboardingBanner from './banner';
import OnboardingChecklist from './OnboardingChecklist';
import { SLACKID_ONBOARDING_MODAL_VISITS_KEY } from './constants/localStorage';

import FeatureFlaggedComponent from 'components/feature-flag-wrapper';

import styles from './styles.module.less';

function ModalContainer() {
  const user = useSelector(selectCurrentUser);

  // Memoize this so that useEffect dependency doesn't cause update loop
  const onboardingModalViews = useMemo(
    () =>
      Number(
        localStorage.getLocalStorageKey(SLACKID_ONBOARDING_MODAL_VISITS_KEY)
      ),
    []
  );

  const [hideModal, setHideModal] = useState(false);
  const [showCompletedBanner, setShowCompletedBanner] = useState(false);
  const [modalIsMinimized, minimizeModal] = useSlackIdOnboardingModalState();

  const openChecklist = () => {
    analytics.track(SLACKID_ONBOARDING_MODAL_VIEW_CHANGED, {
      newState: 'expanded',
    });
    minimizeModal(false);
  };

  const closeChecklist = () => {
    // Only fire event when onboarding is not completed yet
    if (!showCompletedBanner) {
      analytics.track(SLACKID_ONBOARDING_MODAL_VIEW_CHANGED, {
        newState: 'minimized',
      });
    }

    minimizeModal(true);
  };

  useEffect(() => {
    // Increment the number of views
    const numViews = onboardingModalViews || 0;
    const updatedNumViews = numViews + 1;

    localStorage.setLocalStorageKey(
      SLACKID_ONBOARDING_MODAL_VISITS_KEY,
      updatedNumViews
    );
  }, [onboardingModalViews]);

  useEffect(() => {
    if (!hideModal && onboardingModalViews > 5) {
      setHideModal(true);

      analytics.track(SLACKID_ONBOARDING_MODAL_PERMANENTLY_HIDDEN, {
        views: onboardingModalViews,
      });
    }
  }, [onboardingModalViews, hideModal]);

  function openWorkspace() {
    analytics.track(SLACKID_ONBOARDING_MODAL_GO_TO_WORKSPACE);

    window.location.href = MY_VIDEOS_PAGE;
  }

  function renderBanner() {
    if (showCompletedBanner) {
      return (
        <OnboardingBanner
          completed
          emoji={'🚀'}
          title="Congratulations, you're ready to start recording!"
          description="If you need ideas for what to record, check out your workspace for some inspiration and use cases."
          buttonText="Go to Workspace"
          onClick={openWorkspace}
        />
      );
    }

    return (
      <OnboardingBanner
        emoji={'👋'}
        title={`Welcome, ${user.first_name}!`}
        description="Finish setting up your account to start sharing updates,
        feedback, and ideas with video messages."
        buttonVariant="primary"
        buttonText="Finish Account Setup"
        onClick={openChecklist}
      />
    );
  }

  if (hideModal) {
    return null;
  }

  return (
    <>
      {modalIsMinimized && (
        <>
          <div className={styles.bannerSpacing} />
          {renderBanner()}
        </>
      )}
      {!modalIsMinimized && (
        <OnboardingChecklist
          closeChecklist={closeChecklist}
          setShowCompletedBanner={setShowCompletedBanner}
        />
      )}
    </>
  );
}

function SlackIdOnboardingModal() {
  const user = useSelector(selectCurrentUser);

  const isSlackIdUser =
    user?.oauth && Object.keys(user?.oauth).includes(SLACK_OIDC_CONNECT);

  const hasCompletedOnboarding = useSelector(
    state => state?.checklist?.complete_onboarding
  );

  // Exposure requisites:  SlackID user, not completed onboarding, not mobile web
  const experimentIsAllowed = useCallback(() => {
    const reasons = [];

    if (!isSlackIdUser) {
      reasons.push('not a Slack ID user');
    }

    if (isMobile) {
      reasons.push('is on mobile');
    }

    if (hasCompletedOnboarding) {
      reasons.push('has completed onboarding');
    }

    return {
      pass: !isMobile && isSlackIdUser && !hasCompletedOnboarding,
      failReason: reasons.join(','),
    };
  }, [hasCompletedOnboarding, isSlackIdUser]);

  return (
    <FeatureFlaggedComponent
      flag={EXP_SLACKID_ONBOARDING_MODAL}
      activationValues={['variant']}
      eligibilityPreCheckFunction={experimentIsAllowed}
    >
      <ModalContainer />
    </FeatureFlaggedComponent>
  );
}

export default SlackIdOnboardingModal;
